import CampaignStartStopButton from 'components/campaign/restart-with-budget-button/CampaignStartStopButton';
import ChannelButtonGroup from 'components/channel-button-group/ChannelButtonGroup';
import DateButton from 'components/date-button/DateButton';
import MetricGroup from 'components/metrics/metric-group/MetricGroup';
import AppPage from 'components/page/app-page/AppPage';
import SectionHeading from 'components/section/section-heading/SectionHeading';
import { SectionWrapper } from 'components/section/section-wrapper/SectionWrapper';
import SkeletonCardList from 'components/skeleton/skeleton-card-list/SkeletonCardList';
import SkeletonList from 'components/skeleton/skeleton-list/SkeletonList';
import AdList from 'components/social-media/ad-list/AdList';
import AdHistoryPanel from 'components/social-media/ad-history/AdHistoryPanel';
import SocialStats from 'components/social-media/stats/SocialStats';
import { Subhead } from 'components/typography/Typography';
import {
  GET_VACANCY_ADS,
  GET_VACANCY_METRICS,
  GET_VACANCY_TRAJECTORY_PACE,
} from 'graphql/vacancies/queries';
import { useQuery } from 'hooks/sympl-query';
import React, { useEffect, useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import { Ad, AdChannel, AdDateRange } from 'types/adTypes';
import { VacancyMetric, VacancySpendStatus } from 'types/vacancyTypes';
import { dateFilterToNumeric, getMetricDaysFilter } from 'utils/apiHelpers';
import { roundMoney } from 'utils/baseHelpers';

import { filterAdsByChannels, statusToInt } from 'utils/socialMediaHelpers';
import useNavigationContext from 'hooks/context/nav-context';
import useSocialMediaContext from 'hooks/context/social-media-context';
import ChannelToggleGroup from 'components/channel-toggle-group/ChannelToggleGroup';
import ChannelLocationWarning from 'components/channel-location-warning/ChannelLocationWarning';
import PaceControls, {
  CustomPace,
  Pace,
} from 'components/social-media/pacing/PaceControls';
import { useMutation } from 'hooks/sympl-mutation';
import { UPDATE_VACANCY_TRAJECTORY_PACE } from 'graphql/vacancies/mutations';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';

export const SocialMedia: React.FC = () => {
  const { addToast } = useToastNotifications();
  const {
    activeChannels,
    activeDateFilter,
    toggleChannelFilter,
    setDateRangeFilter,
  } = useSocialMediaContext();

  const {
    activeVacancy,
    currentVacancy,
    vacIsPublished: isPublished,
    hasSymplVacancyPage,
  } = useNavigationContext();

  const selectedChannels =
    currentVacancy?.key === activeVacancy ? currentVacancy?.channels ?? [] : [];

  const metricsDayFilter = getMetricDaysFilter();

  const metricPayload = {
    vacancyId: activeVacancy ?? 0,
    days: 0,
    channel: activeChannels.join(','),
  };

  const {
    loading: vacanciesLoading,
    data: vacanciesData,
    refetch: refetchAds,
  } = useQuery<
    {
      ads: Ad[];
    },
    { vacancyId: number }
  >(GET_VACANCY_ADS, {
    skip: !activeVacancy,
    variables: { vacancyId: activeVacancy ?? 0 },
  });

  const {
    loading: metricsLoading,
    data: metricsData,
    refetch: refetchMetrics,
  } = useQuery<
    {
      metrics: VacancyMetric;
    },
    { vacancyId: number; days: number; channel: string }
  >(GET_VACANCY_METRICS, {
    skip: !activeVacancy,
    notifyOnNetworkStatusChange: true,
    variables: {
      ...metricPayload,
      days: metricsDayFilter,
    },
    fetchPolicy: 'cache-and-network',
  });

  const vacBudget = metricsData?.metrics?.vacBudget;
  const totalSpent = roundMoney(metricsData?.metrics?.vacSpent ?? 0);

  const refreshCampaign = async (): Promise<void> => {
    const results = await Promise.allSettled([
      Promise.resolve().then(() => refetchAds()),
      Promise.resolve().then(() => refetchMetrics()),
    ]);
    if (results.some(({ status }) => status === 'rejected')) {
      // Both budget purchases and status changes have already succeeded at this point.
      addToast({
        title: 'Campaign updated',
        description:
          'Your campaign was updated, but some information could not be refreshed. Refresh the page before making another change.',
        type: ToastTypes.WARNING,
      });
    }
  };

  const ads = (vacanciesData?.ads ?? [])
    .filter(
      ({ status }) =>
        status &&
        [
          'INACTIVE',
          'ACTIVE',
          'COMPLETED',
          'ARCHIVED',
          'PAUSED',
          'REVISION',
        ].includes(status)
    )
    .sort(
      (a, b) =>
        statusToInt(b.status!) - statusToInt(a.status!) ||
        b.adMetric!.apmi! - a.adMetric!.apmi!
    );

  const campaignStatus = useMemo(
    () => metricsData?.metrics?.spend_status ?? VacancySpendStatus.INACTIVE,
    [metricsData?.metrics?.spend_status]
  );

  const updateDateFilterHandler = (
    contextSetter: (range: AdDateRange) => void,
    range: AdDateRange
  ) => {
    contextSetter(range);
    refetchMetrics({ ...metricPayload, days: dateFilterToNumeric(range) });
  };

  const budgetSpent = (vacBudget?.total_budget ?? 1) <= totalSpent;

  const [updatePace] = useMutation<
    undefined,
    { vacancyId: number; pace: Pace }
  >(UPDATE_VACANCY_TRAJECTORY_PACE);

  const changePace = async (newPace: string | number) => {
    if (newPace == getPace?.pace) return; // Don't do triple equation since type might be different
    await updatePace({
      variables: {
        vacancyId: activeVacancy!,
        pace: newPace as Pace,
      },
    });
    await paceRefetch({ vacancyId: activeVacancy! });
  };

  const {
    loading: paceLoading,
    data: { getPace } = {},
    refetch: paceRefetch,
  } = useQuery<{ getPace: { pace: Pace | CustomPace } }, { vacancyId: number }>(
    GET_VACANCY_TRAJECTORY_PACE,
    {
      skip: !activeVacancy || !isPublished,
      variables: { vacancyId: activeVacancy! },
    }
  );

  useEffect(() => {
    !!activeVacancy && paceRefetch({ vacancyId: activeVacancy! });
  }, [activeVacancy]);

  // Keep controls and their pending purchase guards mounted when refreshing cached ads.
  return (
    <AppPage
      heading="Social Media"
      loading={vacanciesLoading && !vacanciesData}
      cta={
        <div tw="flex gap-12">
          {!!getPace?.pace && (
            <PaceControls
              loading={paceLoading}
              value={getPace?.pace}
              onChange={changePace}
            />
          )}
          <ChannelToggleGroup />
          {!!isPublished && (
            <CampaignStartStopButton
              id={activeVacancy ?? 0}
              budgetSpent={budgetSpent}
              campaignStatus={campaignStatus}
              onChange={refreshCampaign}
            />
          )}
        </div>
      }
      styles={{
        maxWidth: '100%',
      }}
    >
      <ChannelLocationWarning
        channels={selectedChannels}
        locations={currentVacancy?.targeting?.locations ?? []}
      />
      <ControlsWrapper>
        <div tw="flex flex-col">
          <Subhead isLight mb={1}>
            Filters
          </Subhead>
          <ul tw="flex items-center flex-row">
            <li tw="w-full">
              <ChannelButtonGroup
                channels={activeChannels}
                onChannelClicked={(channel: AdChannel) => {
                  toggleChannelFilter(channel);
                }}
              />
            </li>

            <li tw="w-full sm:(w-auto ml-8 mt-0)">
              <DateButton
                value={activeDateFilter}
                onClear={() =>
                  updateDateFilterHandler(setDateRangeFilter, 'Last quarter')
                }
                onChange={(filter) =>
                  updateDateFilterHandler(
                    setDateRangeFilter,
                    filter as AdDateRange
                  )
                }
              />
            </li>
          </ul>
        </div>
        <div tw="flex gap-8 justify-between">
          <SocialStats
            loading={metricsLoading}
            applications={metricsData?.metrics?.vacApplications}
            spent={totalSpent}
            totalBudget={vacBudget?.total_budget}
            showApplications={hasSymplVacancyPage}
            onSubmit={refetchMetrics}
          />
        </div>
      </ControlsWrapper>

      <SectionWrapper>
        <SectionHeading title="Metrics" structural />
        {metricsLoading ? (
          <SkeletonCardList cards={4} />
        ) : (
          <MetricGroup
            metrics={metricsData?.metrics?.vacMetrics}
            showVacancyPageMetrics={hasSymplVacancyPage}
          />
        )}
      </SectionWrapper>

      <SectionWrapper tw="pb-8">
        <SectionHeading title="Ads" />
        {vacanciesLoading ? (
          <SkeletonList rows={3} />
        ) : (
          <AdList ads={filterAdsByChannels(activeChannels, ads)} />
        )}
        {/* SYM-6351, scene 14: sympl staff only (D35). */}
        <AdHistoryPanel />
      </SectionWrapper>
    </AppPage>
  );
};

const ControlsWrapper = styled.div(
  tw`flex px-1 flex-col-reverse gap-y-2 2xl:flex-row justify-between mb-4 md:mb-11` // Inline padding prevents cutoff of borders
);
